import type { DtoolsStagingLine } from "./dtoolsJsonStaging";

export type DtoolsComponentTier = "PRIMARY_COMPONENT" | "SUPPORTING_COMPONENT";

export type DtoolsClassifiableLine = Pick<
  DtoolsStagingLine,
  | "category"
  | "manufacturer"
  | "model"
  | "polishedDescription"
  | "dtoolsDescription"
  | "isAccessory"
  | "bucket"
  | "subcategory"
>;

const PRIMARY_HINTS =
  /\b(processor|surround processor|avr|receiver|preamp|pre-amp|amplifier|amp\b|loudspeaker|speaker|subwoofer|\bsub\b|projector|screen|display|automation|controller|video processor|matrix|switcher|network core|streamer|media player|bluray|blu-ray|tuner|dac\b|calibration|programming|integration)\b/i;

const SUPPORTING_HINTS =
  /\b(bracket|mount|adapter plate|adapter|hdmi\b|speaker cable|cable\b|connector|patch panel|patch\b|rack shelf|shelf\b|extender|power (strip|supply|dist)|consumable|grommet|backbox|keystone|faceplate|rj45|conduit|tray|bushing|clamp|screw|hardware kit|installation kit|wall plate|gang box)\b/i;

function lineSearchText(line: DtoolsClassifiableLine): string {
  return [
    line.category,
    line.subcategory,
    line.manufacturer,
    line.model,
    line.polishedDescription,
    line.dtoolsDescription,
  ]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

/** Classify a quoted line for customer-facing primary vs supporting presentation. */
export function classifyDtoolsComponentTier(line: DtoolsClassifiableLine): DtoolsComponentTier {
  if (line.isAccessory) return "SUPPORTING_COMPONENT";
  if (line.bucket === "cable") return "SUPPORTING_COMPONENT";

  const haystack = lineSearchText(line);
  if (!haystack) return "PRIMARY_COMPONENT";

  if (SUPPORTING_HINTS.test(haystack)) return "SUPPORTING_COMPONENT";
  if (PRIMARY_HINTS.test(haystack)) return "PRIMARY_COMPONENT";

  if (line.bucket === "service") return "PRIMARY_COMPONENT";

  return "PRIMARY_COMPONENT";
}

export function isPrimaryComponentLine(line: DtoolsClassifiableLine): boolean {
  return classifyDtoolsComponentTier(line) === "PRIMARY_COMPONENT";
}

export function isSupportingComponentLine(line: DtoolsClassifiableLine): boolean {
  return classifyDtoolsComponentTier(line) === "SUPPORTING_COMPONENT";
}
